import { IcPanelClose } from '@assets/icons/24x24';
import { Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { memo, MouseEventHandler, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

type Props = {
  label: string;
  count?: number;
  filter?: ReactNode;
  sort?: ReactNode;
  isFilterDisabled?: boolean;
  marginRight?: number;
  dragManagement?: {
    isDragging: boolean;
    onMouseDown: MouseEventHandler<HTMLDivElement>;
  };
  component?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
};

function PanelHeader({
  label,
  count,
  filter,
  sort,
  isFilterDisabled,
  marginRight = 0,
  dragManagement,
  actions,
  component,
  onClose,
}: Readonly<Props>) {
  function getCursorStyle() {
    if (dragManagement) {
      return dragManagement.isDragging ? 'grabbing' : 'grab';
    }
    return 'default';
  }

  return (
    <VStack
      marginRight={marginRight}
      padding={2}
      gap={2}
      alignSelf="stretch"
      backgroundColor="blue.900"
      flexShrink={0}
      cursor={getCursorStyle()}
      userSelect="none"
      onMouseDown={dragManagement?.onMouseDown}
    >
      <Flex
        alignItems="center"
        alignSelf="stretch"
        borderLeft="8px solid"
        borderColor="blue.600"
        height="32px"
        width="100%"
        boxSizing="border-box"
        backgroundColor="neutral.black"
        userSelect="none"
        gap={1.5}
        textTransform="uppercase"
      >
        <Text color="neutral.200" textStyle="md" fontWeight="medium" marginLeft={1.5} lineClamp={1}>
          {label}
        </Text>
        {count !== undefined && <Text color="skyBlue">{count}</Text>}
        {onClose && (
          <Icon marginLeft="auto" as={IcPanelClose} width="32px" height="32px" cursor="pointer" onClick={onClose} />
        )}
      </Flex>
      {(filter || sort || actions) && (
        <HStack gap={3} width="100%" alignItems="start ">
          {sort && (
            <VStack gap={1} alignItems="start" height="100%">
              <Text color={isFilterDisabled ? 'neutral.300' : 'neutral.white'}>
                <FormattedMessage id="components.panel.sort" />
              </Text>
              {sort}
            </VStack>
          )}
          {filter && (
            <VStack gap={1} alignItems="start" height="100%">
              <Text color={isFilterDisabled ? 'neutral.300' : 'neutral.white'}>
                <FormattedMessage id="components.panel.filter" />
              </Text>
              {filter}
            </VStack>
          )}
          {actions && (
            <HStack gap={0.5} flexShrink={0} marginLeft="auto">
              {actions}
            </HStack>
          )}
        </HStack>
      )}
      {component}
    </VStack>
  );
}

export default memo(PanelHeader);
